import { fmt, pad } from '../util/units.js'

/**
 * The instructor's voice, on screen.
 *
 * This is the part of the trainer the user actually interacts with, so it is
 * worth saying what it is trying to do. Three questions, answered at all
 * times, without anyone having to look for them:
 *
 *   WHAT DO I PRESS?   one key, enormous, in the middle of the bottom edge
 *   WHY?               one sentence under it, in words rather than jargon
 *   WHAT IS NEXT?      the following step, dimmer, so nothing is a surprise
 *
 * Everything else — the checklist down the left, the phase strip across the
 * top, the numbers on the right — is context you can glance at. The card is
 * the only thing you have to read.
 *
 * The card also tells you when it is *not* your turn: informational steps get
 * a different treatment so that "nothing to press" reads as a state rather
 * than as the program having lost track of you.
 */
export class Guidance {
  constructor() {
    this.el = {
      phases: document.getElementById('phases'),
      holdbar: document.getElementById('holdbar'),

      clLeg: document.getElementById('cl-leg'),
      clBrief: document.getElementById('cl-brief'),
      clItems: document.getElementById('cl-items'),

      strip: document.getElementById('strip-items'),

      now: document.getElementById('now'),
      cap: document.getElementById('now-cap'),
      label: document.getElementById('now-label'),
      title: document.getElementById('now-title'),
      keyhint: document.getElementById('now-keyhint'),
      nodcs: document.getElementById('now-nodcs'),
      cue: document.getElementById('now-cue'),
      radio: document.getElementById('now-radio'),
      why: document.getElementById('now-why'),
      next: document.getElementById('now-next'),

      debrief: document.getElementById('debrief'),
      dbGrade: document.getElementById('db-grade'),
      dbPct: document.getElementById('db-pct'),
      dbItems: document.getElementById('db-items'),
      dbList: document.getElementById('db-list'),

      toast: document.getElementById('toast'),
      credits: document.getElementById('credits'),

      keyHelp: document.getElementById('keyhelp'),

      boot: document.getElementById('boot'),
      bootBar: document.getElementById('bootbar'),
      bootStatus: document.getElementById('bootstatus'),
      bootErr: document.getElementById('booterr'),
    }

    document.getElementById('keyhelptoggle')?.addEventListener('click', () => this.toggleHelp())

    this._phaseSig = ''
    this._checklistSig = ''
    this._cardSig = ''
    this._toastUntil = 0
  }

  /* =================================================================== *
   *  BOOT                                                               *
   * =================================================================== */

  progress(pct, message) {
    if (this.el.bootBar) this.el.bootBar.style.width = `${Math.round(pct * 100)}%`
    if (this.el.bootStatus) this.el.bootStatus.textContent = message
  }

  bootComplete() {
    this.el.boot?.classList.add('hidden')
    setTimeout(() => this.el.boot?.remove(), 600)
  }

  bootError(err) {
    if (!this.el.bootErr) return
    this.el.bootErr.hidden = false
    this.el.bootErr.textContent = String(err?.stack ?? err ?? 'unknown error')
    if (this.el.bootStatus) this.el.bootStatus.textContent = 'FAILED TO START'
    if (this.el.bootBar) this.el.bootBar.style.background = '#ff4d4d'
  }

  toggleHelp() {
    this.el.keyHelp?.classList.toggle('collapsed')
  }

  /** Creative Commons attribution for the aircraft model. Licence condition. */
  setCredit(text) {
    if (!this.el.credits) return
    if (!text) {
      this.el.credits.classList.remove('show')
      return
    }
    this.el.credits.textContent = text
    this.el.credits.classList.add('show')
  }

  toast(message, seconds = 1.6, warn = false) {
    if (!this.el.toast) return
    this.el.toast.textContent = message
    this.el.toast.classList.toggle('warn', warn)
    this.el.toast.classList.add('show')
    this._toastUntil = performance.now() / 1000 + seconds
  }

  /* =================================================================== *
   *  FRAME                                                              *
   * =================================================================== */

  /**
   * @param {object} g  snapshot from ProcedureTrainer
   * @param {object} state  the aircraft state, for the numbers strip
   */
  update(g, state) {
    this._phases(g)
    this._checklist(g)
    this._card(g)
    this._strip(state, g)

    this.el.holdbar?.classList.toggle('show', !!g.holding)

    if (this.el.toast?.classList.contains('show') && performance.now() / 1000 > this._toastUntil) {
      this.el.toast.classList.remove('show')
    }
  }

  _phases(g) {
    const sig = `${g.phaseIndex}|${g.phases.length}`
    if (sig === this._phaseSig) return
    this._phaseSig = sig

    this.el.phases.innerHTML = ''
    g.phases.forEach((p, i) => {
      const div = document.createElement('div')
      div.className = `ph ${i === g.phaseIndex ? 'now' : i < g.phaseIndex ? 'done' : ''}`
      div.textContent = p.short
      this.el.phases.appendChild(div)
    })
  }

  _checklist(g) {
    // legLabel, not just legId. The approach turn is one leg but two names —
    // it flips from THE 180 to THE 90 partway round — and leaving the label
    // out of the signature froze this panel on THE 180 for the whole turn
    // while the HUD, which redraws every frame, said THE 90. The two halves of
    // the screen disagreeing about which phase you are in, during the most
    // disorienting part of the pattern.
    const sig =
      g.legLabel +
      g.checklist.map((c) => `${c.id}${c.done ? 1 : 0}${c.late ? 'L' : ''}`).join()
    if (sig === this._checklistSig) return
    this._checklistSig = sig

    this.el.clLeg.textContent = g.legLabel
    this.el.clBrief.textContent = g.legBrief ?? ''

    this.el.clItems.innerHTML = ''
    let firstOpen = true
    for (const item of g.checklist) {
      const li = document.createElement('li')
      const isNow = !item.done && !item.optional && firstOpen
      if (!item.done && !item.optional) firstOpen = false

      li.className = [
        item.done ? 'done' : '',
        item.late ? 'late' : '',
        isNow ? 'now' : '',
        item.optional ? 'optional' : '',
      ]
        .filter(Boolean)
        .join(' ')

      const tick = document.createElement('span')
      tick.className = 'tick'
      tick.textContent = item.done ? (item.late ? '~' : '✓') : isNow ? '▸' : '·'

      const name = document.createElement('span')
      name.textContent = item.title

      li.append(tick, name)

      if (item.key) {
        const k = document.createElement('span')
        k.className = item.noDcsDefault ? 'k nodcs' : 'k'
        k.textContent = item.key
        li.appendChild(k)
      }
      this.el.clItems.appendChild(li)
    }
  }

  _card(g) {
    const step = g.current
    const sig =
      `${step?.id ?? 'none'}|${g.holding ? 1 : 0}|${g.next?.id ?? '-'}|${g.legLabel}` +
      `|${step?.radio ?? ''}`
    if (sig === this._cardSig) return
    this._cardSig = sig

    const now = this.el.now
    now.classList.toggle('holding', !!g.holding)

    if (!step) {
      // Nothing outstanding in this leg. Say so plainly rather than going
      // blank, and point at whatever is coming.
      now.classList.add('info')
      now.classList.remove('optional')
      this.el.cap.textContent = 'STAND BY'
      this.el.label.textContent = g.legLabel
      this.el.title.textContent = 'NOTHING TO DO YET'
      this.el.keyhint.textContent = ''
      this.el.nodcs.hidden = true
      this.el.cue.textContent = g.legBrief ?? ''
      this.el.radio.hidden = true
      this.el.why.textContent = ''
    } else {
      now.classList.toggle('info', !!step.informational)
      now.classList.toggle('optional', !!step.optional)
      this.el.cap.textContent = step.key ?? 'WATCH'
      this.el.label.textContent = g.holding
        ? 'WAITING FOR YOU'
        : step.informational
          ? g.legLabel
          : step.optional
            ? 'IF DESIRED'
            : 'DO THIS NOW'
      this.el.title.textContent = step.title
      this.el.keyhint.textContent = step.keyHint ?? ''
      this.el.cue.textContent = step.cue ?? ''
      if (step.radio) {
        this.el.radio.hidden = false
        this.el.radio.textContent = `“${step.radio}”`
      } else {
        this.el.radio.hidden = true
      }
      this.el.why.textContent = step.why ?? ''
    }

    // ---- the NEXT strip -------------------------------------------------
    this.el.next.innerHTML = ''
    if (g.next) {
      const lead = document.createElement('span')
      lead.textContent = 'NEXT'
      const key = document.createElement('span')
      key.className = 'k'
      key.textContent = g.next.key ?? '—'
      const title = document.createElement('b')
      title.textContent = g.next.title
      const where = document.createElement('span')
      where.textContent =
        g.next.leg === g.legId ? '' : `· ${legWord(g.next.leg)}`
      this.el.next.append(lead, key, title, where)
    } else {
      const lead = document.createElement('span')
      lead.textContent = `${g.done} of ${g.total} complete`
      this.el.next.append(lead)
    }
  }

  _strip(state, g) {
    if (!state) return
    const rows = [
      ['SPEED', `${fmt(state.kcas, 0)} kt`, false],
      ['ALT', `${fmt(state.altFt, 0)} ft`, false],
      ['AoA', `${fmt(state.aoaUnits, 1)} u`, Math.abs(state.aoaUnits - 15) > 1.2],
      ['HDG', pad(state.headingDeg), false],
      ['V/S', `${fmt(state.vsFpm, 0)} fpm`, false],
      ['GEAR', posWord(state.gearNorm), state.gearNorm > 0.02 && state.gearNorm < 0.98],
      ['FLAPS', posWord(state.flapNorm), state.flapNorm > 0.02 && state.flapNorm < 0.98],
      ['HOOK', posWord(state.hookNorm), state.hookNorm > 0.02 && state.hookNorm < 0.98],
      ['BOARDS', state.speedbrakeNorm > 0.5 ? 'OUT' : 'IN', state.speedbrakeNorm > 0.5],
      ['THROTTLE', `${fmt(state.throttleNorm * 100, 0)}%`, false],
      ['BRAKES', state.wheelBrakes ? 'ON' : 'OFF', !!state.wheelBrakes],
      ['SWEEP', `${fmt(state.sweepDeg, 0)}\u00b0`, state.sweepDeg > 22],
      ['DLC', g?.systems?.dlcArmed ? 'ARMED' : 'OFF', !!g?.systems?.dlcArmed],
      ['AUTO THR', g?.systems?.apcEngaged ? 'ENGAGED' : 'OFF', false],
      ['SAS', g?.systems?.sasOn ? 'ON' : 'OFF', !g?.systems?.sasOn],
    ]
    const sig = rows.map((r) => r[1]).join('|') + (g?.holding ? 'H' : '')
    if (sig === this._stripSig) return
    this._stripSig = sig

    this.el.strip.innerHTML = ''
    for (const [k, v, warn] of rows) {
      const dt = document.createElement('dt')
      dt.textContent = k
      const dd = document.createElement('dd')
      dd.textContent = v
      if (warn) dd.className = 'warn'
      this.el.strip.append(dt, dd)
    }
  }

  /* =================================================================== *
   *  DEBRIEF                                                            *
   * =================================================================== */

  showDebrief(d) {
    if (!this.el.debrief) return
    this.el.debrief.classList.add('show')
    this.el.dbGrade.textContent = d.grade
    this.el.dbPct.textContent = `${d.scorePct}%  ·  ${d.onTime} of ${d.total} on time`

    const rows = [
      ['WIRE', d.wire ? `${d.wire}` : '—'],
      ['GROOVE', `${d.grooveTimeS.toFixed(1)} s`],
      ['TOUCHDOWN', d.touchdownFt ? `${d.touchdownFt.y >= 0 ? '' : '-'}${Math.abs(d.touchdownFt.y).toFixed(0)} ft of centre` : '—'],
      ['PASS TIME', `${Math.round(d.elapsedS)} s`],
      ['WRONG KEYS', `${d.fumbles}`],
    ]
    this.el.dbItems.innerHTML = ''
    for (const [k, v] of rows) {
      const dt = document.createElement('dt')
      dt.textContent = k
      const dd = document.createElement('dd')
      dd.textContent = v
      this.el.dbItems.append(dt, dd)
    }

    const notes = []
    if (d.late.length) notes.push(`Held for: ${d.late.join(', ')}`)
    if (d.missed.length) notes.push(`Never done: ${d.missed.join(', ')}`)
    if (d.optionalTaken?.length) notes.push(`Also taken: ${d.optionalTaken.join(', ')}`)
    if (!notes.length) notes.push('Every item ahead of the aeroplane. That is the pass.')
    this.el.dbList.innerHTML = notes.map((n) => `<div>${n}</div>`).join('')
  }

  hideDebrief() {
    this.el.debrief?.classList.remove('show')
  }
}

const posWord = (n) => (n > 0.98 ? 'DOWN' : n < 0.02 ? 'UP' : 'MOVING')

function legWord(legId) {
  return (
    {
      INITIAL: 'at the initial',
      BREAK: 'in the break',
      DOWNWIND: 'downwind',
      APPROACH_TURN: 'in the turn',
      GROOVE: 'in the groove',
      TOUCHDOWN: 'at touchdown',
      ROLLOUT: 'on rollout',
    }[legId] ?? ''
  )
}
